import React, { useState, useEffect, useMemo, useCallback } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useParams, Link } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';

import api from '../../services/api';
import { readItem } from '../../utils/apiResult';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import Tabs from '../../components/ui/Tabs';
import ApplicationOverviewTab from './tab-pages/ApplicationOverviewTab';
import ApplicationResumeTab from './tab-pages/ApplicationResumeTab';
import ApplicationHistoryTab from './tab-pages/ApplicationHistoryTab';
import ApplicationReferenceChecksTab from './tab-pages/ApplicationReferenceChecksTab';

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const StickyHeader = styled.div`
  flex-shrink: 0;
`;

const TabContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding-top: 8px;
`;

// FE-23 — okuma başarısız olduğunda çizilen kart. Sekmeler DEĞİL: alt sekmelerin
// hepsi `application == null` durumunu "Yükleniyor..." diye çiziyordu ve o durum
// hiç bitmiyordu (kapsam dışı/silinmiş başvuru = 404). Kullanıcı "sistem yavaş"
// diyordu; gerçek sebep 404'tü.
const ErrorCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
  margin: 24px 0;
  padding: 18px 20px;
  border-radius: ${({ theme }) => theme.metrics.radius.large};
  border: 1px solid ${({ theme }) => theme.colors.auxiliar.danger}33;
  background: ${({ theme }) => theme.colors.auxiliar.danger}14;
  color: ${({ theme }) => theme.colors.auxiliar.danger};
`;

const ErrorHeadline = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  font-size: ${({ theme }) => theme.metrics.font.size.md};
`;

const ErrorHint = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.text.dark.little};
`;

const BackLink = styled(Link)`
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  color: ${({ theme }) => theme.colors.primary.main};
`;

export default function AtsApplicationDetailsPage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const [application, setApplication] = useState(null);
  const [candidate, setCandidate] = useState(null);
  const [interviews, setInterviews] = useState([]);
  const [loadFailed, setLoadFailed] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  // Refetch the application (and its candidate). Exposed to the Overview tab so a
  // stage move done there refreshes currentStage/Type — keeping the reference
  // card and stage rows in sync without a full page reload.
  //
  // FE-23: `.catch()` burada ÖLÜ KODDU — services/api.js okuma yollarında ASLA
  // reject etmez, `{status:false, data:null}` ile RESOLVE eder. Gerçek sinyal
  // `res.data.status` (utils/apiResult.js). Eskiden `application` sonsuza kadar
  // null kalıyor ve her sekme "Yükleniyor..." çiziyordu.
  const loadApplication = useCallback(() => {
    api.get(`/v1/ats-applications/${id}`)
      .then((res) => {
        const a = readItem(res);
        setLoadFailed(a == null);
        setApplication(a);
        if (a?.candidateId) {
          api.get(`/v1/ats-candidates/${a.candidateId}`)
            .then((cRes) => setCandidate(readItem(cRes)));
        } else {
          setCandidate(null);
        }
      });
  }, [id]);

  useEffect(() => { loadApplication(); }, [loadApplication]);

  // Başvurunun mülakatları — Genel Bakış sekmesi bunu HİÇ çekmiyordu; "Mülakat
  // Planla" düğmesi yalnız başvuru durumuna bakıyor, mevcut mülakattan habersiz
  // çiziliyordu (İK farkında olmadan ikinci mülakat planlayabiliyordu).
  // Filtre sözleşmesi AtsInterviewController.GetAll'dan okundu: `applicationId[eq]`.
  // Sunucu kapsam dışındaki aktöre boş liste/hata dönebilir — okuma yollarında
  // api.js reject ETMEZ, bu yüzden gerçek sinyal `res.data.status`tür.
  const loadInterviews = useCallback(() => {
    api.get(`/v1/ats-interviews?applicationId[eq]=${id}&pageSize=100&orderBy=scheduledStart&orderDir=desc`)
      .then((res) => {
        const d = res?.data?.data;
        const list = Array.isArray(d) ? d : (d?.data || []);
        setInterviews(Array.isArray(list) ? list : []);
      });
  }, [id]);

  useEffect(() => { loadInterviews(); }, [loadInterviews]);

  const tabs = useMemo(() => [
    { key: 'overview', label: t('ats.application.tab.overview') },
    { key: 'resume', label: t('ats.application.tab.resume') },
    { key: 'references', label: t('ats.application.tab.references') },
    { key: 'history', label: t('ats.application.tab.history') },
  ], [t]);

  const breadcrumbLabel = application?.candidateFullName
    ? `${application.candidateFullName} — ${application.jobPostingTitle}`
    : '...';

  return (
    <PageContainer>
      <StickyHeader>
        <Breadcrumbs items={[
          { label: t('ats.application.pageTitle'), path: '/ats/applications' },
          { label: loadFailed ? t('ats.application.notFoundTitle') : breadcrumbLabel },
        ]} />
        {!loadFailed && <Tabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} />}
      </StickyHeader>

      <TabContent>
        {loadFailed && (
          <ErrorCard role="alert" data-role="ats-application-load-error">
            <ErrorHeadline>
              <AlertTriangle size={18} />
              {t('ats.application.notFoundTitle')}
            </ErrorHeadline>
            <ErrorHint>{t('ats.application.notFoundHint')}</ErrorHint>
            <BackLink to="/ats/applications">{t('ats.application.backToList')}</BackLink>
          </ErrorCard>
        )}

        {!loadFailed && activeTab === 'overview' && (
          <ApplicationOverviewTab
            application={application}
            candidate={candidate}
            interviews={interviews}
            onInterviewsChanged={loadInterviews}
            onNavigateToReferences={() => setActiveTab('references')}
            onApplicationChanged={loadApplication}
          />
        )}
        {!loadFailed && activeTab === 'resume' && (
          <ApplicationResumeTab candidate={candidate} />
        )}
        {!loadFailed && activeTab === 'references' && (
          <ApplicationReferenceChecksTab applicationId={id} />
        )}
        {!loadFailed && activeTab === 'history' && (
          <ApplicationHistoryTab applicationId={id} />
        )}
      </TabContent>
    </PageContainer>
  );
}
